import { useEffect, useMemo, useState } from 'react'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { collection, getDocs } from 'firebase/firestore'
import { app, db, COLLECTIONS } from '../firebase/config'
import toast from 'react-hot-toast'
import { Phone, MessageCircle, Link as LinkIcon, Users, Loader, Send } from 'lucide-react'
import PageHelpButton from '../components/PageHelpButton'

const normalizePhone233 = (phone) => {
  if (!phone) return ''
  let clean = phone.replace(/\s+/g, '').trim()
  clean = clean.replace(/[^\d+]/g, '')
  if (clean.startsWith('+')) {
    clean = clean.substring(1)
  } else if (clean.startsWith('0')) {
    clean = clean.replace(/^0/, '233')
  } else if (!clean.startsWith('233')) {
    clean = '233' + clean
  }
  clean = clean.replace(/\D/g, '')
  if (!clean.startsWith('233') || clean.length !== 12) return ''
  return clean
}

const estimateSegments = (message) => {
  if (!message) return 1
  const len = message.length
  return Math.max(1, Math.ceil(len / 150))
}

const Messaging = () => {
  // Manual payment link
  const [mpPhone, setMpPhone] = useState('')
  const [mpName, setMpName] = useState('')
  const [mpAmount, setMpAmount] = useState('')
  const [sendingLink, setSendingLink] = useState(false)

  // Bulk SMS
  const [smsBalance, setSmsBalance] = useState(null)
  const [balanceLoading, setBalanceLoading] = useState(false)
  const [useCustomers, setUseCustomers] = useState(true)
  const [customerPhones, setCustomerPhones] = useState([])
  const [loadingCustomers, setLoadingCustomers] = useState(false)
  const [manualNumbers, setManualNumbers] = useState('')
  const [bulkMessage, setBulkMessage] = useState('')
  const [sendingBulk, setSendingBulk] = useState(false)

  const functionsInstance = useMemo(() => getFunctions(app), [])

  const fetchBalance = async () => {
    try {
      setBalanceLoading(true)
      const fn = httpsCallable(functionsInstance, 'getSmsBalance')
      const res = await fn()
      const data = res.data || {}
      setSmsBalance(typeof data.balance === 'number' ? data.balance : Number(data.balance) || 0)
    } catch (e) {
      console.error('Failed to fetch SMS balance', e)
      toast.error('Failed to fetch SMS balance')
    } finally {
      setBalanceLoading(false)
    }
  }

  useEffect(() => {
    fetchBalance()
  }, [])

  const handleSendLink = async (e) => {
    e.preventDefault()
    const clean = normalizePhone233(mpPhone)
    if (!clean) {
      toast.error('Enter a valid Ghana phone (05… / 0… / 233…)')
      return
    }
    const amountNumber = mpAmount ? Number(mpAmount) : null
    if (mpAmount && (Number.isNaN(amountNumber) || amountNumber <= 0)) {
      toast.error('Amount must be a positive number')
      return
    }
    try {
      setSendingLink(true)
      const fn = httpsCallable(functionsInstance, 'sendManualBalancePaymentLinkSMS')
      await fn({
        phone: clean,
        name: mpName || undefined,
        amount: amountNumber || undefined,
      })
      toast.success('Payment link SMS sent')
      // Clear form after successful send
      setMpName('')
      setMpPhone('')
      setMpAmount('')
    } catch (e) {
      console.error('Failed to send link SMS', e)
      const msg = e?.message || 'Failed to send payment link SMS'
      toast.error(msg)
    } finally {
      setSendingLink(false)
    }
  }

  const loadCustomers = async () => {
    try {
      setLoadingCustomers(true)
      const snap = await getDocs(collection(db, COLLECTIONS.CUSTOMERS))
      const setPhones = new Set()
      snap.forEach((doc) => {
        const d = doc.data()
        const p = normalizePhone233(d.phone)
        if (p) setPhones.add(p)
      })
      setCustomerPhones(Array.from(setPhones))
      toast.success(`Loaded ${setPhones.size} customer numbers`)
    } catch (e) {
      console.error('Failed to load customers', e)
      toast.error('Failed to load customers')
    } finally {
      setLoadingCustomers(false)
    }
  }

  const resolvedRecipients = useMemo(() => {
    if (useCustomers) return customerPhones
    const raw = manualNumbers
    if (!raw.trim()) return []
    const parts = raw
      .split(/[\n,;]/)
      .map((p) => normalizePhone233(p))
      .filter(Boolean)
    return Array.from(new Set(parts))
  }, [useCustomers, customerPhones, manualNumbers])

  const segments = useMemo(() => estimateSegments(bulkMessage), [bulkMessage])
  const estimatedCost = segments * resolvedRecipients.length

  const handleSendBulk = async (e) => {
    e.preventDefault()
    if (!bulkMessage.trim()) {
      toast.error('Enter a message to send')
      return
    }
    if (resolvedRecipients.length === 0) {
      toast.error('No valid phone numbers to send to')
      return
    }
    try {
      setSendingBulk(true)
      const fn = httpsCallable(functionsInstance, 'sendBulkSMS')
      const res = await fn({
        phones: resolvedRecipients,
        message: bulkMessage,
      })
      const data = res.data || {}
      await fetchBalance()
      toast.success(
        `Bulk SMS sent to ${data.sent || resolvedRecipients.length} recipients`
      )
      // Clear message and manual list (keep loaded customers)
      setBulkMessage('')
      if (!useCustomers) {
        setManualNumbers('')
      }
    } catch (e) {
      console.error('Failed to send bulk SMS', e)
      const code = e?.code || ''
      const msg = e?.message || ''
      if (code === 'functions/failed-precondition') {
        toast.error(msg || 'Insufficient SMS balance. Contact developer.')
      } else {
        toast.error(msg || 'Failed to send bulk SMS')
      }
    } finally {
      setSendingBulk(false)
    }
  }

  return (
    <div className="space-y-8">
      <PageHelpButton
        videoId={8}
        title="Messaging & Links tutorial"
        youtubeUrl="https://www.youtube.com/embed/1MkWDJwtx2E"
      />

      {/* Manual payment link section */}
      <div className="bg-gray-800 bg-opacity-60 rounded-lg border border-gray-700 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center">
            <LinkIcon className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white">Send Manual Payment Link</h2>
            <p className="text-sm text-gray-300">
              Send a one-time manual balance payment link to a customer via SMS. Amount is optional.
            </p>
          </div>
        </div>

        <form onSubmit={handleSendLink} className="grid gap-4 md:grid-cols-3">
          <div className="md:col-span-1">
            <label className="block text-sm font-medium text-gray-200 mb-1">
              Customer Name (optional)
            </label>
            <input
              type="text"
              value={mpName}
              onChange={(e) => setMpName(e.target.value)}
              placeholder="e.g. Ama"
              className="w-full px-3 py-2 rounded-md bg-gray-900 border border-gray-700 text-white text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          <div className="md:col-span-1">
            <label className="block text-sm font-medium text-gray-200 mb-1">
              Phone Number
            </label>
            <div className="relative">
              <span className="absolute inset-y-0 left-3 flex items-center text-gray-500 text-xs">
                <Phone className="w-4 h-4 mr-1" /> 0 / 233
              </span>
              <input
                type="tel"
                value={mpPhone}
                onChange={(e) => setMpPhone(e.target.value)}
                placeholder="05XXXXXXXX or 233XXXXXXXXX"
                className="w-full pl-16 pr-3 py-2 rounded-md bg-gray-900 border border-gray-700 text-white text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="md:col-span-1">
            <label className="block text-sm font-medium text-gray-200 mb-1">
              Amount (GHS, optional)
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={mpAmount}
              onChange={(e) => setMpAmount(e.target.value)}
              placeholder="Leave empty to let client choose"
              className="w-full px-3 py-2 rounded-md bg-gray-900 border border-gray-700 text-white text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          <div className="md:col-span-3 flex justify-end">
            <button
              type="submit"
              disabled={sendingLink}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-purple-600 hover:bg-purple-500 text-white text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {sendingLink ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span>Send Payment Link SMS</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Bulk SMS section */}
      <div className="bg-gray-800 bg-opacity-60 rounded-lg border border-gray-700 p-6">
        <div className="flex flex-wrap items-center gap-4 mb-4 justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-pink-600 flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Send Bulk SMS</h2>
              <p className="text-sm text-gray-300">
                Send general updates or announcements to customers or a custom list.
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-400 mb-1">
              150 characters ≈ 1 SMS per recipient (special characters may cost more)
            </p>
            <p className="text-sm text-gray-200">
              SMS Balance:{' '}
              {balanceLoading ? (
                <span className="inline-flex items-center gap-1 text-gray-300">
                  <Loader className="w-3 h-3 animate-spin" /> checking…
                </span>
              ) : smsBalance != null ? (
                <span className="font-semibold text-green-400">{smsBalance}</span>
              ) : (
                <span className="text-gray-400">–</span>
              )}
            </p>
          </div>
        </div>

        <form onSubmit={handleSendBulk} className="space-y-4">
          {/* Recipient source */}
          <div className="flex flex-wrap items-center gap-4">
            <div className="inline-flex rounded-full bg-gray-900 border border-gray-700 p-1">
              <button
                type="button"
                onClick={() => setUseCustomers(true)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                  useCustomers ? 'bg-purple-600 text-white' : 'text-gray-300'
                }`}
              >
                Use synced customers
              </button>
              <button
                type="button"
                onClick={() => setUseCustomers(false)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                  !useCustomers ? 'bg-purple-600 text-white' : 'text-gray-300'
                }`}
              >
                Enter numbers manually
              </button>
            </div>

            {useCustomers && (
              <button
                type="button"
                onClick={loadCustomers}
                disabled={loadingCustomers}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-gray-900 border border-gray-700 text-xs text-gray-100 disabled:opacity-60"
              >
                {loadingCustomers ? (
                  <>
                    <Loader className="w-3 h-3 animate-spin" />
                    <span>Loading…</span>
                  </>
                ) : (
                  <>
                    <Users className="w-3 h-3" />
                    <span>Load from Customers</span>
                  </>
                )}
              </button>
            )}
          </div>

          {/* Recipients input */}
          {!useCustomers && (
            <div>
              <label className="block text-sm font-medium text-gray-200 mb-1">
                Phone numbers
              </label>
              <textarea
                value={manualNumbers}
                onChange={(e) => setManualNumbers(e.target.value)}
                rows={4}
                placeholder="One number per line or separated by comma/semicolon. Formats: 05…, 0…, 233…"
                className="w-full px-3 py-2 rounded-md bg-gray-900 border border-gray-700 text-sm text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          )}

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-gray-200 mb-1">
              Message
            </label>
            <textarea
              value={bulkMessage}
              onChange={(e) => setBulkMessage(e.target.value)}
              rows={5}
              className="w-full px-3 py-2 rounded-md bg-gray-900 border border-gray-700 text-sm text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Type your message…"
            />
            <div className="mt-1 flex flex-wrap items-center justify-between text-xs text-gray-400 gap-2">
              <span>
                Length: <span className="font-semibold text-gray-200">{bulkMessage.length}</span>{' '}
                chars · Segments:{' '}
                <span className="font-semibold text-gray-200">{segments}</span>
              </span>
              <span>
                Recipients:{' '}
                <span className="font-semibold text-gray-200">
                  {resolvedRecipients.length}
                </span>{' '}
                · Estimated cost:{' '}
                <span className="font-semibold text-gray-200">{estimatedCost}</span>
              </span>
            </div>
          </div>

          {/* Send button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={
                sendingBulk ||
                !bulkMessage.trim() ||
                resolvedRecipients.length === 0
              }
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-pink-600 hover:bg-pink-500 text-white text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {sendingBulk ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  <span>Sending SMS…</span>
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span>Send Bulk SMS</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default Messaging

